import React from "react";
import { IChatNotification } from "common/broadcast/_stores/chat/interfaces";
import { BroadcastType } from "common/broadcast/_stores/broadcast/enums";
import { IBroadcastData } from "common/broadcast/_stores/broadcast/interfaces";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import LocalBar from "@material-ui/icons/LocalBar";
import TailwindIcon from "library/components/_tailwind/icon";
import CustomIcon from "library/components/_tailwind/icon/icons";
import { CustomIconName } from "library/components/_tailwind/icon/icons/enums";
import { inject, observer } from "mobx-react";
import ChatStore from "common/broadcast/_stores/chat/ChatStore";
import AiChatStore from "common/broadcast/_stores/ai-chat/AiChatStore";
import { languageStore } from "library/core/stores/language/LanguageStore";
import { resolveNotificationParams } from "common/broadcast/_stores/chat/notification-helpers";

const broadcastTypes = new Map<BroadcastType, IBroadcastData>([
  [
    BroadcastType.Tipped,
    {
      className: "notificationTipped",
      icon: (fontSize: number) => (
        <MonetizationOnOutlinedIcon
          style={{ width: fontSize, height: fontSize }}
        />
      ),
    } as IBroadcastData,
  ],
  [
    BroadcastType.Gift,
    {
      className: "notificationGift",
      icon: (fontSize: number) => (
        <LocalBar style={{ width: fontSize, height: fontSize }} />
      ),
    } as IBroadcastData,
  ],
  [
    BroadcastType.Buzz,
    {
      className: "notificationBuzz",
      icon: (fontSize: number) => (
        <TailwindIcon
          name={CustomIconName.notificationBuzz}
          style={{ fontSize: `${fontSize}px` }}
        />
      ),
    } as IBroadcastData,
  ],
  [
    BroadcastType.SuperBuzz,
    {
      className: "notificationSuperBuzz",
      icon: (fontSize: number) => (
        <TailwindIcon
          name={CustomIconName.notificationSuperBuzz}
          style={{ fontSize: `${fontSize}px` }}
        />
      ),
    } as IBroadcastData,
  ],
  [
    BroadcastType.BroadcastStart,
    {
      className: "notificationBroadcastingInHD",
    } as IBroadcastData,
  ],
  [
    BroadcastType.BroadcastStop,
    {
      className: "notificationBroadcastingStop",
    } as IBroadcastData,
  ],
  [
    BroadcastType.ShowStart,
    {
      className: "notificationShowStart",
    } as IBroadcastData,
  ],
  [
    BroadcastType.ShowStop,
    {
      className: "notificationShowStop",
    } as IBroadcastData,
  ],
  [
    BroadcastType.WheelOfFun,
    {
      className: "notificationWheel",
      icon: (fontSize: number) => (
        <TailwindIcon
          name={CustomIconName.wheelOfFunIcon}
          style={{ width: `${fontSize}px`, height: `${fontSize}px` }}
        />
      ),
    } as IBroadcastData,
  ],
  [
    BroadcastType.TopAdmirer,
    {
      className: "notificationAdmirer",
      icon: (fontSize: number) => (
        <TailwindIcon
          name={CustomIconName.medal}
          iconProps={{ iconrange: 1 }}
          style={{ fontSize: `${fontSize}px` }}
        />
      ),
    } as IBroadcastData,
  ],
]);

const omittedNotifications = ["notificationMessage.broadcastStarted"];

const aiIconBroadcastTypes = new Set([
  BroadcastType.Tipped,
  BroadcastType.Gift,
  BroadcastType.Buzz,
  BroadcastType.SuperBuzz,
  BroadcastType.WheelOfFun,
]);

const getAiSystemMessage = (
  broadcastType: BroadcastType,
  username: string,
  params?: any
): string => {
  switch (broadcastType) {
    case BroadcastType.Tipped:
      return `[SYSTEM] ${username} tipped ${params?.chatValue || ""} tokens`;
    case BroadcastType.Gift:
      return `[SYSTEM] ${username} sent a gift: ${params?.product || "unknown"}, ${params?.chatValue || 0} tokens`;
    case BroadcastType.SuperBuzz:
      return `[SYSTEM] ${username} sent a super buzz`;
    case BroadcastType.Buzz:
      return `[SYSTEM] ${username} sent a buzz`;
    case BroadcastType.WheelOfFun:
      return `[SYSTEM] ${username} spun the wheel`;
    default:
      return "";
  }
};

type Props = {
  notification: IChatNotification;
  chatStore?: ChatStore;
  aiChatStore?: AiChatStore;
  isMemberTabNotification?: boolean;
  isMainChatTab?: boolean;
};

const ChatNotification: React.ComponentType<Props> = ({
  notification,
  chatStore,
  aiChatStore,
  isMemberTabNotification,
  isMainChatTab,
}) => {
  // Read intl from the MobX-observable languageStore so the component
  // re-renders the moment the model toggles language. Going through
  // react-intl's IntlContext alone has proven unreliable across the
  // observer/inject HOC stack on language change, leaving stale text
  // in place until something else triggers a re-render.
  const { intl } = languageStore;
  const { chatFontSize: fontSize, chatGiftImageSize } = chatStore!;

  const showAiIcon =
    aiChatStore!.isUiVisible &&
    notification.broadcastType !== undefined &&
    aiIconBroadcastTypes.has(notification.broadcastType);

  const getText = (notification: IChatNotification): string => {
    const result = notification.fixedContentKey
      ? intl.formatMessage(
          {
            id: notification.fixedContentKey,
            defaultMessage: notification.content,
          },
          resolveNotificationParams(notification, intl)
        )
      : notification.content;

    return result || "";
  };

  const getImage = (notification: IChatNotification, fontSize: number) => {
    if (notification.image && (isMemberTabNotification || isMainChatTab)) {
      return (
        <img
          src={notification.image}
          alt={notification.content}
          style={{
            width: chatGiftImageSize,
            height: chatGiftImageSize,
            margin: "5px 10px",
          }}
        />
      );
    } else if (
      notification.image &&
      !isMemberTabNotification &&
      !isMainChatTab
    ) {
      return (
        <img
          src={notification.image}
          alt={notification.content}
          style={{
            width: chatGiftImageSize,
            height: chatGiftImageSize,
            margin: "5px 10px",
          }}
        />
      );
    } else if (broadcastTypes.get(notification.broadcastType!)?.icon) {
      return broadcastTypes.get(notification.broadcastType!)?.icon(fontSize);
    } else {
      return null;
    }
  };

  const isGiftNotification =
    notification.broadcastType === BroadcastType.Gift;

  return omittedNotifications.includes(notification.fixedContentKey!) ? null : (
    <div
      className={`ChatNotification ${
        broadcastTypes.get(notification.broadcastType!)?.className || ""
      } ${isMemberTabNotification || isMainChatTab ? "" : "background-muted"}`}
      data-testid={"chat-message-notification"}
      style={{ fontSize }}>
      {isGiftNotification && getImage(notification, fontSize)}
      {intl.formatMessage({
        id: "broadcast.overlay.systemPrefix",
        defaultMessage: "[System]: ",
      })}
      &nbsp;
      {!isGiftNotification && (
        <>{getImage(notification, fontSize)}&nbsp;</>
      )}
      <div
        style={{ fontSize }}
        dangerouslySetInnerHTML={{
          __html: getText(notification),
        }}
      />
      {showAiIcon && notification.fixedContentParams?.chatfrom && (
        <span
          style={{
            fontSize: "0.875rem",
            color: "var(--pink-color)",
            display: "inline-flex",
            verticalAlign: "middle",
            cursor: "pointer",
            marginLeft: "4px",
            opacity: 0.6,
          }}
          onClick={() => {
            const username = notification.fixedContentParams.chatfrom;
            aiChatStore?.requestTransactionRecommendation(
              username,
              getAiSystemMessage(
                notification.broadcastType!,
                username,
                notification.fixedContentParams
              )
            );
          }}>
          <CustomIcon name={"auto_awesome"} />
        </span>
      )}
    </div>
  );
};

export default inject(
  "chatStore",
  "aiChatStore"
)(observer(ChatNotification));
